import React, { createContext, useContext, ReactNode, useMemo, useRef } from 'react';
import i18n from 'i18next';
import { ErrorHandlerContext } from './ErrorHandlerProvider';
import { ApiConfig, ApiResponse, AppName } from '../types';
import { translateError, isPresentableMessage } from '../utils/errorMessages';
import { clearMaintenanceState, isMaintenanceResponse, setMaintenanceActive } from '../utils/maintenanceState';

const translateErrorCode = (code: string): string | null => {
    if (!/^\d+$/.test(code)) return null;
    const key = `errorcode.${code}`;
    return i18n.exists(key) ? (i18n.t(key) as string) : null;
};

/**
 * The bearer token the client sends has changed.
 *
 * Every path that changes it — login, a restored session, `refreshToken()`'s
 * re-signed token, logout — goes through `setAuthToken` / `removeAuthToken`, so
 * this is the one place that sees all of them. `AuthProvider` exposes no token
 * on its context, and it has local copies in the two browser apps, so a
 * subscription here is the only signal an app-side hook can key off.
 *
 * Plain module state and no React: a listener is typically a hook mounted once
 * for the life of the app, and this has to fire from inside a bare function.
 * Nothing here touches react-native, so the browser bundles stay clean.
 */
type AuthTokenListener = (token: string | null) => void;

const authTokenListeners = new Set<AuthTokenListener>();

export const subscribeAuthTokenChange = (listener: AuthTokenListener): (() => void) => {
    authTokenListeners.add(listener);
    return () => { authTokenListeners.delete(listener); };
};

const notifyAuthTokenChange = (token: string | null): void => {
    authTokenListeners.forEach((listener) => {
        try {
            listener(token);
        } catch {
            // One bad subscriber must not stop the others from being told, and
            // must never be able to break a login.
        }
    });
};

export interface RequestOptions {
    /** Override the default request timeout (ms). Use for slow endpoints like route saves. */
    timeoutMs?: number;
}

interface ApiClientContextType {
    get: <T = any>(endpoint: string, headers?: Record<string, string>, options?: RequestOptions) => Promise<ApiResponse<T>>;
    post: <T = any>(endpoint: string, data?: any, headers?: Record<string, string>, options?: RequestOptions) => Promise<ApiResponse<T>>;
    put: <T = any>(endpoint: string, data?: any, headers?: Record<string, string>, options?: RequestOptions) => Promise<ApiResponse<T>>;
    delete: <T = any>(endpoint: string, headers?: Record<string, string>, options?: RequestOptions) => Promise<ApiResponse<T>>;
    patch: <T = any>(endpoint: string, data?: any, headers?: Record<string, string>, options?: RequestOptions) => Promise<ApiResponse<T>>;
    upload: <T = any>(
        endpoint: string,
        file: { uri: string; type: string; name: string },
        additionalData?: Record<string, any>,
        headers?: Record<string, string>,
        options?: RequestOptions
    ) => Promise<ApiResponse<T>>;
    setAuthToken: (token: string) => void;
    removeAuthToken: () => void;
    /**
     * Which app this bundle is, straight from ApiConfig. Consumers that talk to the
     * API's per-app version gate (login, /check/version) read it from here rather
     * than importing an app-specific environment module into shared code.
     */
    appName?: AppName;
}

export const ApiClientContext = createContext<ApiClientContextType | undefined>(undefined);

interface ApiClientProviderProps {
    children: ReactNode;
    config: ApiConfig;
}

export const ApiClientProvider: React.FC<ApiClientProviderProps> = ({ 
    children, 
    config 
}) => {
    const errorHandlerContext = useContext(ErrorHandlerContext);
    const handleApiError = errorHandlerContext?.handleApiError || ((error: any) => ({
        success: false,
        error: error?.message || translateError('generic'),
    }));

    // Default headers with auth token support
    const defaultHeaders = useMemo(() => ({
        'Content-Type': 'application/json',
        ...config.headers,
    }), [config.headers]);

    /**
     * The auth token lives in a ref, and ONLY in a ref.
     *
     * `makeRequest` is a plain function rebuilt on every render, so reading the state
     * value would bind whatever token existed in the render that produced the copy of
     * `get`/`post` a caller is holding. Several consumers deliberately hold ONE copy
     * for the life of a screen -- `usePlanCounts`, RouteScreen's `refreshReactivations`,
     * ScheduleScreen's `refreshScheduleCount` are all `useCallback(..., [])`, and they
     * are memoised on purpose, because `useApiClient()` returns fresh function
     * identities every render and depending on them re-fires the fetch forever.
     *
     * The consequence was silent and long-lived: a callback frozen before the token
     * landed (or before a refresh replaced it) kept sending the old header for as long
     * as its component stayed mounted. Symptom seen 2026-09-03 -- /route/count,
     * /schedule/count and /user/limits/me all answering 4001 in the same burst in which
     * /notifications and /user/subscription/me, called from fresh closures, answered
     * 200. Those three are the plan-limit counters, so the gates then compare against
     * zero, which is the exact failure PLAN-plan-limits-v4 3.3 added the hook to avoid.
     *
     * A ref has one current value regardless of which render's closure asks, so a
     * memoised caller is no longer a stale caller. It was state as well until now, but
     * nothing rendered off it — the re-render bought nothing and having two copies of
     * the token invited exactly the drift described above.
     */
    const authTokenRef = useRef<string | null>(null);

    // Set authorization header
    const setAuthToken = (token: string) => {
        // Only on a real change: `saveTokens` runs on every session restore too, and
        // a subscriber that refetches would then do so on every launch for nothing.
        const changed = authTokenRef.current !== token;
        authTokenRef.current = token;
        if (changed) notifyAuthTokenChange(token);
    };

    // Remove authorization header
    const removeAuthToken = () => {
        const changed = authTokenRef.current !== null;
        authTokenRef.current = null;
        if (changed) notifyAuthTokenChange(null);
    };

    // Make request helper
    const makeRequest = async (
        method: string,
        endpoint: string,
        data?: any,
        customHeaders?: Record<string, string>,
        options?: RequestOptions,
    ): Promise<Response> => {
        const url = `${config.baseUrl}${endpoint}`;
        console.log(`[ApiClient] ${method} ${url}`, data ? { data } : '');
        const headers: Record<string, string> = {
            ...defaultHeaders,
            ...customHeaders,
        };

        // Add auth token if available, read at SEND time rather than captured — see
        // authTokenRef above. An explicit Authorization in customHeaders now wins over
        // it: the only two callers that pass one (AuthProvider's fetchUserFromApi and
        // refreshToken) read the token straight from storage and mean that one
        // specifically, and refreshToken re-signs whichever token it sends.
        const currentToken = authTokenRef.current;
        if (currentToken && !headers['Authorization']) {
            headers['Authorization'] = `Bearer ${currentToken}`;
        }

        const requestConfig: RequestInit = {
            method,
            headers,
        };

        if (data && method !== 'GET') {
            if (data instanceof FormData) {
                // Remove Content-Type for FormData to let the browser set it
                delete headers['Content-Type'];
                requestConfig.body = data;
            } else {
                requestConfig.body = JSON.stringify(data);
            }
        }

        const controller = new AbortController();
        const timeoutMs = options?.timeoutMs ?? config.timeout ?? 10000;
        const timeoutId = setTimeout(() => {
            console.warn(`[ApiClient] Aborting request after ${timeoutMs}ms timeout for ${url}`);
            try {
                controller.abort();
            } catch (e) {
                // Ignore abort errors
            }
        }, timeoutMs);
        requestConfig.signal = controller.signal;

        const startTime = Date.now();
        try {
            console.log(`[ApiClient] Starting ${method} request to ${url}...`);
            // Use React Native's fetch implementation
            // In SDK 55/RN 0.83.2, fetch should work natively
            const response = await fetch(url, requestConfig);
            const duration = Date.now() - startTime;
            clearTimeout(timeoutId);
            console.log(`[ApiClient] Response status: ${response.status} for ${method} ${url} (took ${duration}ms)`);
            return response;
        } catch (error: any) {
            clearTimeout(timeoutId);
            console.error(`[ApiClient] Request failed for ${method} ${url}:`, error);
            
            // Thrown errors are surfaced to the user, so their `message` must stay generic -
            // no URLs, hosts or timings. Technical detail goes to `detail` (logs only).
            // Handle AbortError gracefully - it's a timeout, not a real error
            if (error?.name === 'AbortError' || error?.message?.includes('aborted') || error?.message?.includes('timeout')) {
                console.warn(`[ApiClient] Request timed out after ${timeoutMs}ms for ${url}`);
                // Create a plain error object without using Error constructor to avoid DOMException issues
                const timeoutError: any = {
                    name: 'TimeoutError',
                    message: translateError('timeout'),
                    detail: `Request timeout after ${timeoutMs}ms for ${method} ${url}`,
                };
                timeoutError.toString = () => timeoutError.message;
                throw timeoutError;
            }
            // Check for network errors
            if (error?.message?.includes('Network request failed') || error?.message?.includes('Failed to connect')) {
                console.error(`[ApiClient] Network error - check if server is running at ${url}`);
                const networkError: any = {
                    name: 'NetworkError',
                    message: translateError('network'),
                    detail: `Unable to connect to ${url}`,
                };
                networkError.toString = () => networkError.message;
                throw networkError;
            }
            const genericError: any = {
                name: error?.name || 'ApiError',
                message: translateError('generic'),
                detail: error?.message || String(error),
            };
            genericError.toString = () => genericError.message;
            throw genericError;
        }
    };

    // Handle response helper
    const handleResponse = async <T,>(response: Response): Promise<ApiResponse<T>> => {
        const contentType = response.headers.get('content-type');
        const isJson = contentType && contentType.includes('application/json');

        let responseData: any;
        if (isJson) {
            // A JSON content-type with an empty body (e.g. 204 No Content, or an
            // empty 200) makes response.json() throw "Unexpected end of input".
            // Read as text first and only parse when there's something to parse.
            const text = await response.text();
            responseData = text ? JSON.parse(text) : null;
        } else {
            responseData = await response.text();
        }

        if (response.ok) {
            // The platform is answering, so any maintenance window we were showing is
            // over. Cheap: a no-op unless the flag is actually set.
            clearMaintenanceState();
            return {
                success: true,
                data: responseData,
            };
        } else {
            // Maintenance mode. The guard sends the operator's own wording as the body
            // message and 5xx bodies are otherwise discarded below, so this is the one
            // place it can be kept. The request still fails and still returns an error
            // — the caller does not need to know why — but the app can now show the
            // window instead of "could not connect".
            if (isMaintenanceResponse(response.status, responseData)) {
                setMaintenanceActive(String(responseData?.message ?? ''));
            }
            const rawMessage = responseData?.message;
            const errorCode = typeof rawMessage === 'string' && /^\d+$/.test(rawMessage) ? rawMessage : undefined;
            const translated = errorCode ? translateErrorCode(errorCode) : null;
            console.error(`[ApiClient] HTTP ${response.status}`, responseData);
            // Fall back to a generic message rather than echoing a raw body: 5xx pages and
            // framework defaults expose the URL and other internals to the user.
            const fallback = response.status >= 500 ? translateError('server') : translateError('generic');
            const serverMessage = response.status < 500
                ? [responseData?.descMessage, rawMessage].find(isPresentableMessage)
                : undefined;
            return {
                success: false,
                error: translated || serverMessage || fallback,
                errorCode,
                data: responseData,
            };
        }
    };

    // GET request
    const get = async <T = any>(endpoint: string, headers?: Record<string, string>, options?: RequestOptions): Promise<ApiResponse<T>> => {
        try {
            const response = await makeRequest('GET', endpoint, undefined, headers, options);
            return await handleResponse<T>(response);
        } catch (error) {
            return handleApiError(error, 'GET', endpoint);
        }
    };

    // POST request
    const post = async <T = any>(
        endpoint: string,
        data?: any,
        headers?: Record<string, string>,
        options?: RequestOptions,
    ): Promise<ApiResponse<T>> => {
        try {
            const response = await makeRequest('POST', endpoint, data, headers, options);
            return await handleResponse<T>(response);
        } catch (error) {
            return handleApiError(error, 'POST', endpoint);
        }
    };

    // PUT request
    const put = async <T = any>(
        endpoint: string,
        data?: any,
        headers?: Record<string, string>,
        options?: RequestOptions,
    ): Promise<ApiResponse<T>> => {
        try {
            const response = await makeRequest('PUT', endpoint, data, headers, options);
            return await handleResponse<T>(response);
        } catch (error) {
            return handleApiError(error, 'PUT', endpoint);
        }
    };

    // DELETE request
    const deleteRequest = async <T = any>(endpoint: string, headers?: Record<string, string>, options?: RequestOptions): Promise<ApiResponse<T>> => {
        try {
            const response = await makeRequest('DELETE', endpoint, undefined, headers, options);
            return await handleResponse<T>(response);
        } catch (error) {
            return handleApiError(error, 'DELETE', endpoint);
        }
    };

    // PATCH request
    const patch = async <T = any>(
        endpoint: string,
        data?: any,
        headers?: Record<string, string>,
        options?: RequestOptions,
    ): Promise<ApiResponse<T>> => {
        try {
            const response = await makeRequest('PATCH', endpoint, data, headers, options);
            return await handleResponse<T>(response);
        } catch (error) {
            return handleApiError(error, 'PATCH', endpoint);
        }
    };

    // Upload file
    const upload = async <T = any>(
        endpoint: string,
        file: { uri: string; type: string; name: string },
        additionalData?: Record<string, any>,
        headers?: Record<string, string>,
        options?: RequestOptions,
    ): Promise<ApiResponse<T>> => {
        try {
            const formData = new FormData();
            formData.append('file', file as any);

            if (additionalData) {
                Object.keys(additionalData).forEach(key => {
                    formData.append(key, additionalData[key]);
                });
            }

            const response = await makeRequest('POST', endpoint, formData, {
                ...headers,
            }, options);

            return await handleResponse<T>(response);
        } catch (error) {
            return handleApiError(error, 'UPLOAD', endpoint);
        }
    };

    const value: ApiClientContextType = {
        get,
        post,
        put,
        delete: deleteRequest,
        patch,
        upload,
        setAuthToken,
        removeAuthToken,
        appName: config.appName,
    };

    return (
        <ApiClientContext.Provider value={value}>
            {children}
        </ApiClientContext.Provider>
    );
};

export const useApiClient = (): ApiClientContextType => {
    const context = useContext(ApiClientContext);
    if (context === undefined) {
        throw new Error('useApiClient must be used within an ApiClientProvider');
    }
    return context;
};
